import React, { useEffect } from "react";
import styles from "./CheckoutSection.module.scss";
import DynamicFontAwesomeIcon from "components/elements/DynamicFontAwesomeIcon";
import Input from "components/elements/Input/Input";
import PreferencesSelector from "./PreferencesSelector";

/**
 * Component for displaying and managing a single ticket in the checkout
 */
const TicketItem = ({ 
	ticket, 
	formData, 
	formErrors, 
	onTicketChange, 
	onClearError 
}) => {
	// Effect to ensure preference selector is shown when ticket is for orderer and needs preferences
	useEffect(() => {
		// This ensures the preferences section is rendered when the ticket is initially for the orderer
		if (ticket.isForOrderer && 
			ticket.requiredAgendaAmount > 0 && 
			(!ticket.preferences || ticket.preferences.length < ticket.requiredAgendaAmount)) {
			
				// Check if we have available agendas and log if missing
				if (!ticket.availableAgendas || ticket.availableAgendas.length === 0) {
					console.warn('Ticket requires preferences but has no available agendas:', ticket);
				}
		}
	}, [ticket]);

	const handleTicketAssignmentChange = (isForOrderer) => {
		const updatedTicket = {
			...ticket,
			isForOrderer,
			invitee: isForOrderer ? {
				email: formData.email,
				firstName: formData.name,
				lastName: formData.lastName
			} : {
				email: "",
				firstName: "",
				lastName: ""
			},
			// Keep preferences when switching to "for me" if they exist,
			// otherwise reset them to empty array
			preferences: isForOrderer ? ticket.preferences : []
		};

		onTicketChange(updatedTicket);
		
		// Clear any errors related to this ticket
		const errorKeys = Object.keys(formErrors).filter(key => key.includes(ticket.id));
		errorKeys.forEach(key => onClearError(key));
	};

	const handleInviteeFieldChange = (field, value) => {
		const updatedTicket = {
			...ticket,
			invitee: {
				...ticket.invitee,
				[field]: value
			}
		};
		
		onTicketChange(updatedTicket);
		
		// Clear specific error when editing field
		if (formErrors[`ticket_${field}_${ticket.id}`]) {
			onClearError(`ticket_${field}_${ticket.id}`);
		}
	};

	return (
		<div className={`${styles.ticketAssignmentContainer} mt-3 p-3 rounded-3`}>
			<div className="d-flex justify-content-between align-items-center mb-2">
				<h6 className="d-flex align-items-center mb-0">
					<DynamicFontAwesomeIcon 
						iconName="ticket" 
						className="me-2" 
					/>
					<span className="small fw-semibold">Ticket {ticket.ticketNumber}</span>
				</h6>
				<div 
					className="btn-group" 
					role="group" 
					aria-label={`Ticketzuweisung für Ticket ${ticket.ticketNumber}`}>
					<input
						type="radio"
						className="btn-check"
						name={`ticketFor_${ticket.id}`}
						id={`forMe_${ticket.id}`}
						checked={ticket.isForOrderer}
						onChange={() => handleTicketAssignmentChange(true)}
						aria-label="Ticket für mich"
					/>
					<label 
						className="btn btn-sm btn-outline-light" 
						htmlFor={`forMe_${ticket.id}`}
						title="Dieses Ticket für mich reservieren">
						<DynamicFontAwesomeIcon 
							iconName="user" 
							className="me-2" 
						/>
						Für mich
					</label>
					<input
						type="radio"
						className="btn-check"
						name={`ticketFor_${ticket.id}`}
						id={`forOther_${ticket.id}`}
						checked={!ticket.isForOrderer}
						onChange={() => handleTicketAssignmentChange(false)}
						aria-label="Ticket für andere Person"
					/>
					<label 
						className="btn btn-sm btn-outline-light" 
						htmlFor={`forOther_${ticket.id}`}
						title="Dieses Ticket für jemand anderen reservieren">
						<DynamicFontAwesomeIcon 
							iconName="userPlus" 
							className="me-2" 
						/>
						Für andere
					</label>
				</div>
			</div>

			{/* Show invitee fields for tickets assigned to others */}
			{!ticket.isForOrderer && (
				<div className={`${styles.inviteeFields} mt-3`}>
					<div className="row g-2">
						<div className="col-lg-4">
							<Input
								type="email"
								id={`ticket_email_${ticket.id}`}
								name={`ticket_email_${ticket.id}`}
								size="sm"
								floating={false}
								variant="light"
								placeholder="E-Mail-Adresse"
								value={ticket.invitee.email}
								onChange={(e) => handleInviteeFieldChange("email", e.target.value)}
								error={formErrors[`ticket_email_${ticket.id}`]}
								required={true}
								aria-label="E-Mail-Adresse des Teilnehmers"
								aria-required="true"
							/>
						</div>
						<div className="col-lg-4">
							<Input
								type="text"
								id={`ticket_firstName_${ticket.id}`}
								name={`ticket_firstName_${ticket.id}`}
								size="sm"
								floating={false}
								variant="light"
								placeholder="Vorname"
								value={ticket.invitee.firstName}
								onChange={(e) => handleInviteeFieldChange("firstName", e.target.value)}
								error={formErrors[`ticket_firstName_${ticket.id}`]}
								required={true}
								aria-label="Vorname des Teilnehmers"
								aria-required="true"
							/>
						</div>
						<div className="col-lg-4">
							<Input
								type="text"
								id={`ticket_lastName_${ticket.id}`}
								name={`ticket_lastName_${ticket.id}`}
								size="sm"
								floating={false}
								variant="light"
								placeholder="Nachname"
								value={ticket.invitee.lastName}
								onChange={(e) => handleInviteeFieldChange("lastName", e.target.value)}
								error={formErrors[`ticket_lastName_${ticket.id}`]}
								required={true}
								aria-label="Nachname des Teilnehmers"
								aria-required="true"
							/>
						</div>
					</div>
				</div>
			)}
			
			{/* Render preferences for tickets assigned to orderer */}
			{ticket.isForOrderer && ticket.requiredAgendaAmount > 0 && (
				<PreferencesSelector 
					ticket={ticket} 
					onTicketChange={onTicketChange}
					formErrors={formErrors}
					onClearError={onClearError}
				/>
			)}
		</div>
	);
};

export default TicketItem;
